import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { Card, Spinner, fmtDate, gbp, gbp2 } from "@/components/portal/shared";

const Bar = ({ label, value, max, money }) => (
  <div className="flex items-center gap-3 py-1.5">
    <span className="w-40 shrink-0 truncate text-xs text-slate-600">{label}</span>
    <div className="flex-1 h-2.5 rounded-full bg-warm-100 overflow-hidden">
      <div className="h-full rounded-full bg-navy-900" style={{ width: `${max ? Math.max(3, (value / max) * 100) : 0}%` }} />
    </div>
    <span className="w-20 shrink-0 text-right text-xs font-semibold text-navy-900">{money ? gbp(value) : value}</span>
  </div>
);

const BarList = ({ data, money }) => {
  const entries = Object.entries(data || {});
  const max = Math.max(0, ...entries.map(([, v]) => v));
  if (entries.length === 0) return <p className="text-sm text-slate-400">No data yet.</p>;
  return <div>{entries.map(([k, v]) => <Bar key={k} label={k} value={v} max={max} money={money} />)}</div>;
};

export default function ReportsPage() {
  const { api, user } = usePortalAuth();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [health, setHealth] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (user === false) { navigate("/portal/login", { replace: true }); return; }
    if (user) {
      api.get("/reports").then((x) => setR(x.data)).catch(() => setDenied(true));
      api.get("/cron-health").then((x) => setHealth(x.data)).catch(() => setHealth(null));
    }
  }, [api, user, navigate]);

  if (denied)
    return (
      <div className="rounded-2xl border border-warm-200 bg-white p-10 text-center shadow-sm" data-testid="reports-denied">
        <p className="font-serif text-xl text-navy-900 mb-2">No access</p>
        <p className="text-sm text-slate-500">Reports are available to admin and office roles only.</p>
      </div>
    );
  if (r === null) return <Spinner />;

  const quoteRate = r.quotes_total ? Math.round((r.quotes_accepted / r.quotes_total) * 100) : 0;
  const kpis = [
    ["Total leads", r.leads_total], ["Quotes created", r.quotes_total],
    ["Quote conversion", `${quoteRate}%`], ["Orders", r.orders_total],
    ["Average order", gbp2(r.avg_order)], ["Deposits received", gbp(r.deposits_received)],
    ["Total outstanding", gbp(r.outstanding)],
  ];

  return (
    <div data-testid="reports-page" className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {kpis.map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-warm-200 bg-white p-4 shadow-sm" data-testid={`report-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5 leading-tight">{label}</p>
            <p className="font-serif text-xl text-navy-900">{value}</p>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Leads by Source" testid="report-by-source"><BarList data={r.leads_by_source} /></Card>
        <Card title="Top Products" testid="report-by-product"><BarList data={r.leads_by_product} /></Card>
        <Card title="Sales by Month" testid="report-by-month"><BarList data={r.sales_by_month} money /></Card>
        <Card title="Orders by Status" testid="report-by-status"><BarList data={r.orders_by_status} /></Card>
        <Card title="Quotes by Staff Member" testid="report-by-staff"><BarList data={r.quotes_by_staff} /></Card>
        <Card title="Installs by Fitter" testid="report-by-installer"><BarList data={r.installs_by_installer} /></Card>
      </div>

      <Card title="System Health — Scheduled Emails & Sync" testid="cron-health-card">
        {!health ? (
          <p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Checking…</p>
        ) : (
          <>
            <ul className="divide-y divide-warm-200">
              {health.jobs.map((j) => {
                const outcome = j.last_run
                  ? j.job === "visit-reminders"
                    ? `${j.last_run.sent ?? 0} reminder${(j.last_run.sent ?? 0) === 1 ? "" : "s"} sent`
                    : j.job === "review-requests"
                      ? `${j.last_run.sent ?? 0} review email${(j.last_run.sent ?? 0) === 1 ? "" : "s"} sent`
                      : `${j.last_run.leads_resynced ?? 0} re-synced · ${j.last_run.stages_synced ?? 0} stages updated`
                  : "Never run yet";
                return (
                  <li key={j.job} className="py-3.5 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`cron-job-${j.job}`}>
                    <span className="min-w-36">
                      <span className="block text-sm font-semibold text-slate-900 capitalize">{j.job.replace(/-/g, " ")}</span>
                      <span className="block text-xs text-slate-400">{j.schedule}</span>
                    </span>
                    {j.last_run ? (
                      j.last_run.done ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-bold text-emerald-900"><CheckCircle2 className="h-3 w-3" /> OK</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-900"><Clock className="h-3 w-3" /> Running</span>
                      )
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold text-slate-500">Pending</span>
                    )}
                    <span className="text-xs text-slate-600">{outcome}</span>
                    <span className="ml-auto text-xs text-slate-400">{j.last_run ? fmtDate(j.last_run.received_at) : ""}</span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-4 pt-4 border-t border-warm-200 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
              <span data-testid="cron-pending-queue">Sync queue pending: <strong className={health.pending_sync_queue > 0 ? "text-amber-700" : "text-emerald-700"}>{health.pending_sync_queue}</strong></span>
              <span data-testid="cron-failed-leads">HubSpot failed leads: <strong className={health.hubspot_failed_leads > 0 ? "text-amber-700" : "text-emerald-700"}>{health.hubspot_failed_leads}</strong></span>
            </div>
          </>
        )}
      </Card>
      <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><BarChart3 className="h-3 w-3" /> Figures update live from your orders, quotes and enquiries.</p>
    </div>
  );
}
